import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Stepper,
  Text,
  ThemeIcon,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { DataTable } from "mantine-datatable";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconArrowsSort,
  IconCheck,
  IconGripVertical,
  IconUsers,
} from "@tabler/icons-react";
import {
  Player,
  TournamentTeam,
  TournamentTeamMemberDto,
  TournamentTeamStatus,
} from "../../../../../gql/graphql";
import { useTeamsFromTournament } from "../../../../../business/teams.hook";
import { SEED_TOURNAMENT_STEP_MUTATION } from "../seeding/seeding.operations";

const SELECT_TEAMS_MODAL_ID = "select-teams-modal";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(420),
  },
  tableWrapper: {
    backgroundColor: theme.colors.dark[6],
    borderRadius: theme.radius.md,
    overflow: "hidden",
  },
  membersList: {
    color: theme.colors.dark[2],
    fontSize: theme.fontSizes.xs,
  },
}));

type TeamWithMembers = Omit<TournamentTeam, "members"> & {
  members: (TournamentTeamMemberDto & { player: Player })[];
};

interface SelectTeamsModalProps {
  readonly tournamentId: string;
  readonly stepId: string;
  readonly onSeeded?: () => void;
}

type SelectTeamsStep = 0 | 1;

export const SelectTeamsModal = ({
  tournamentId,
  stepId,
  onSeeded,
}: SelectTeamsModalProps) => {
  const { classes } = useStyles();
  const { loading, results } = useTeamsFromTournament({
    status: TournamentTeamStatus.Confirmed,
    tournamentId,
  });

  const [wizardStep, setWizardStep] = useState<SelectTeamsStep>(0);
  const [selectedRecords, setSelectedRecords] = useState<TeamWithMembers[]>([]);
  const [orderedTeams, setOrderedTeams] = useState<TeamWithMembers[]>([]);

  const [seedStep, { loading: seeding }] = useMutation(
    SEED_TOURNAMENT_STEP_MUTATION,
  );

  const teamsById = useMemo(
    () => new Map<string, TeamWithMembers>(results.map((t) => [t.id, t])),
    [results],
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sourceIdx = result.source.index;
    const destIdx = result.destination.index;
    if (sourceIdx === destIdx) return;
    const next = Array.from(orderedTeams);
    const [moved] = next.splice(sourceIdx, 1);
    next.splice(destIdx, 0, moved);
    setOrderedTeams(next);
  };

  const advanceToOrderStep = () => {
    // Initialize the orderedTeams from the current selection, preserving the
    // order in which the user selected them (DataTable returns records in the
    // selection order).
    setOrderedTeams(
      selectedRecords
        .map((r) => teamsById.get(r.id))
        .filter((t): t is TeamWithMembers => !!t),
    );
    setWizardStep(1);
  };

  const handleSeed = async () => {
    try {
      const orderedTeamIds = orderedTeams.map((t) => t.id);
      const result = await seedStep({
        variables: {
          input: {
            stepId,
            orderedTeamIds,
          },
        },
      });
      if (result?.error) {
        throw new Error(result.error.message);
      }
      notifications.show({
        title: "Step seeded",
        message: `Seeded ${orderedTeamIds.length} team${orderedTeamIds.length !== 1 ? "s" : ""} into the step.`,
        color: "green",
        icon: <IconCheck size={16} />,
      });
      onSeeded?.();
      modals.close(SELECT_TEAMS_MODAL_ID);
    } catch (err) {
      notifications.show({
        title: "Seeding failed",
        message: err instanceof Error ? err.message : "Unexpected error",
        color: "rose",
      });
    }
  };

  if (loading) {
    return (
      <Stack align="center" justify="center" className={classes.container}>
        <Loader color="cyan" size="lg" />
        <Text c="dimmed" size="sm">
          Loading teams...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack className={classes.container} gap="md">
      <Group justify="space-between">
        <Group>
          <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
            <IconUsers size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>Seed teams</Text>
            <Text size="xs" c="dimmed">
              Select teams and confirm the seeding order. Pins can be added
              later from the Seeding panel on the step dashboard.
            </Text>
          </div>
        </Group>
        {selectedRecords.length > 0 && (
          <Badge color="cyan" variant="light" size="lg">
            {selectedRecords.length} selected
          </Badge>
        )}
      </Group>

      <Stepper active={wizardStep} size="xs">
        <Stepper.Step label="Select teams" icon={<IconUsers size={14} />} />
        <Stepper.Step
          label="Confirm order"
          icon={<IconArrowsSort size={14} />}
        />
      </Stepper>

      {wizardStep === 0 && (
        <Stack gap="md">
          <Alert
            color="blue"
            variant="light"
            icon={<IconAlertCircle size={16} />}
          >
            Select the confirmed teams that should participate in this step.
            Seeding behaviour (distribution, group allocation, placement) is
            driven by the step&apos;s preset rule set.
          </Alert>

          <div className={classes.tableWrapper}>
            <ScrollArea h={280}>
              <DataTable<TeamWithMembers>
                records={results}
                striped
                highlightOnHover
                withColumnBorders
                columns={[
                  {
                    accessor: "name",
                    title: "Team Name",
                    render: (record) => (
                      <Group gap="xs">
                        <Text fw={500}>{record.name}</Text>
                      </Group>
                    ),
                  },
                  {
                    accessor: "members",
                    title: "Members",
                    render: (record) => (
                      <Text className={classes.membersList}>
                        {record.members
                          .map((member) => member.player.username)
                          .join(", ")}
                      </Text>
                    ),
                  },
                ]}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
              />
            </ScrollArea>
          </div>

          <Group justify="flex-end">
            <Button
              rightSection={<IconArrowRight size={16} />}
              disabled={selectedRecords.length === 0}
              onClick={advanceToOrderStep}
            >
              Continue ({selectedRecords.length})
            </Button>
          </Group>
        </Stack>
      )}

      {wizardStep === 1 && (
        <Stack gap="md">
          <Alert
            color="blue"
            variant="light"
            icon={<IconArrowsSort size={16} />}
          >
            Drag to adjust the seed order. The preset&apos;s{" "}
            <Text component="span" fw={600}>
              SEEDING
            </Text>{" "}
            rule will consume this order when placing teams into groups and
            game slots.
          </Alert>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="seed-order">
              {(provided) => (
                <ScrollArea.Autosize mah={360}>
                  <Stack
                    gap="xs"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {orderedTeams.map((team, idx) => (
                      <Draggable
                        key={team.id}
                        draggableId={team.id}
                        index={idx}
                        isDragDisabled={seeding}
                      >
                        {(drag, snapshot) => (
                          <Card
                            withBorder
                            padding="sm"
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            style={{
                              ...drag.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "var(--mantine-color-dark-5)"
                                : undefined,
                            }}
                          >
                            <Group gap="sm">
                              <div
                                {...drag.dragHandleProps}
                                aria-label={`Reorder ${team.name}`}
                                style={{ cursor: "grab", display: "flex" }}
                              >
                                <IconGripVertical size={18} />
                              </div>
                              <Badge variant="light" color="cyan">
                                #{idx + 1}
                              </Badge>
                              <Text fw={500}>{team.name}</Text>
                            </Group>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                </ScrollArea.Autosize>
              )}
            </Droppable>
          </DragDropContext>

          <Group justify="space-between">
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              disabled={seeding}
              onClick={() => setWizardStep(0)}
            >
              Back
            </Button>
            <Button
              leftSection={<IconCheck size={16} />}
              loading={seeding}
              disabled={orderedTeams.length === 0}
              onClick={handleSeed}
            >
              Seed {orderedTeams.length} team
              {orderedTeams.length !== 1 ? "s" : ""}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
};

// Helper to open the modal
export const openSelectTeamsModal = (props: SelectTeamsModalProps) => {
  modals.open({
    modalId: SELECT_TEAMS_MODAL_ID,
    title: null,
    centered: true,
    size: "xl",
    children: <SelectTeamsModal {...props} />,
  });
};
